import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { isTopbarMenuPageActive } from '../currentPageMatch';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { AUCTIONS_SEARCH_PARAMS, createSearchPageTo } from '../../../../util/search';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={classNames(css.topbarLink, css.signupLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={classNames(css.topbarLink, css.signupLink)}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const routeName = inboxTab === 'sales' ? 'SalesPage' : 'OrdersPage';

  return (
    <NamedLink className={css.topbarLink} name={routeName}>
      <FormattedMessage id="TopbarDesktop.inbox" />
      {notificationDot}
    </NamedLink>
  );
};

const ProfileMenu = ({
  currentPage,
  currentUser,
  onLogout,
  showManageListingsLink,
  showSellerVerificationLink,
  showAdminVerificationLink,
  isSellerVerificationApproved,
}) => {
  const currentPageClass = page => {
    return isTopbarMenuPageActive(currentPage, page) ? css.currentPage : null;
  };

  const sellerVerificationStatusBadge = isSellerVerificationApproved ? (
    <span className={css.statusBadge}>Approved</span>
  ) : null;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>

      <MenuContent className={css.profileMenuContent}>
        {showManageListingsLink ? (
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
        ) : null}
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.favoritesLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>{' '}
        </MenuItem>

        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>

        {showSellerVerificationLink ? (
          <MenuItem key="SellerVerificationPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SellerVerificationPage'))}
              name="SellerVerificationPage"
            >
              <span className={css.menuItemBorder} />
              <span className={css.menuLinkWithBadge}>
                <span>Seller Verification</span>
                {sellerVerificationStatusBadge}
              </span>
            </NamedLink>
          </MenuItem>
        ) : null}

        {showAdminVerificationLink ? (
          <MenuItem key="AdminVerificationPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AdminVerificationPage'))}
              name="AdminVerificationPage"
            >
              <span className={css.menuItemBorder} />
              Admin Verification
            </NamedLink>
          </MenuItem>
        ) : null}

        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    viewport,
    showSellerVerificationLink,
    showAdminVerificationLink,
    isSellerVerificationApproved,
    createListingTarget,
  } = props;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated;
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;

  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const isChefProfilePublished = currentUser?.attributes?.profile?.publicData?.isPublished;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      showSellerVerificationLink={showSellerVerificationLink}
      showAdminVerificationLink={showAdminVerificationLink}
      isSellerVerificationApproved={isSellerVerificationApproved}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  return (
    <nav className={classes}>
      <div className={css.mainTopHeader}>
        <div className={css.fixedWidthContainer}>
          <div className={css.leftNav}>
            <LinkedLogo
              className={css.logoLink}
              layout="desktop"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </div>

          <div className={css.middleNav}>
            {isChefProfilePublished && (
              <CustomLinksMenu
                currentPage={currentPage}
                customLinks={customLinks}
                intl={intl}
                hasClientSideContentReady={
                  authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
                }
                showCreateListingsLink={showCreateListingsLink}
                currentUser={currentUser}
              />
            )}

            <NamedLink
              name="SearchPage"
              className={css.topbarLink}
              to={createSearchPageTo(AUCTIONS_SEARCH_PARAMS)}
            >
              <FormattedMessage id="TopbarDesktop.nav.auctions" />
            </NamedLink>

            <NamedLink name="SearchPage" className={css.topbarLink} to={createSearchPageTo()}>
              <FormattedMessage id="TopbarDesktop.nav.buyNow" />
            </NamedLink>

            <NamedLink name="FinancePage" className={css.topbarLink}>
              <FormattedMessage id="TopbarDesktop.nav.finance" />
            </NamedLink>

            <NamedLink name="ContactPage" className={css.topbarLink}>
              <FormattedMessage id="TopbarDesktop.nav.contactUs" />
            </NamedLink>

            <NamedLink name="AboutUsPage" className={css.topbarLink}>
              <FormattedMessage id="TopbarDesktop.nav.about" />
            </NamedLink>
          </div>

          <div className={css.rightNav}>
            <NamedLink
              name={createListingTarget?.name || 'NewListingPage'}
              params={createListingTarget?.params}
              to={createListingTarget?.to}
              className={css.topbarLink}
            >
              <FormattedMessage id="TopbarDesktop.nav.startSelling" />
            </NamedLink>
            {inboxLinkMaybe}

            {loginLinkMaybe}
            {/* {signupLinkMaybe} */}
            {profileMenuMaybe}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
